import i18nFactory from '@yandex-int/i18n';
import { isEmpty, isNumber } from 'lodash';
import moment, { Moment } from 'moment';

import {
    FilterSuggestControlFocus,
    FilterSuggestControlFocusValue,
    visibleTextItemsCount
} from '../../components/FilterControl/FilterSuggest/FilterSuggest';
import {
    FilterBooleanValueType,
    getBannerFilterCriteriaContainers,
    getCampaignFilterCriteriaContainers,
    getFeedFilterFilterCriteriaContainers,
    getGroupFilterCriteriaContainers,
    getPhraseFilterCriteriaContainers,
    getTargetingFilterCriteriaContainers,
    getUserProfileFilterCriteriaContainers
} from '../../declarations/filter/filter';
import {
    expressionToString,
    getOperatorsForCriteriaType,
    operatorToString
} from '../../mappers/filter/filter';
import {
    banners,
    campaigns,
    ClientInfo,
    DateRangePreset,
    feedFilters,
    groups,
    phrases,
    retargetings,
    usersProfile
} from '../../typings/common';
import {
    Criteria,
    CriteriaContainer,
    CriteriaType,
    EnumSetItem,
    FilterExpressionValue,
    FilterExpressionValueData,
    FilterExpressionValueType,
    FilterPartialExpression,
    Operator,
    ParsedFilterPartialExpression,
    SuggestStep
} from '../../typings/filter';
import { PeriodFilterValue } from '../../typings/filters';
import { Goal } from '../../typings/goals';
import {
    GridScreenUrlSearchQueryParams,
    GridStatPeriod,
    GridViewType
} from '../../typings/grid';
import { parse } from '../FilterParser/parser';
import { parseNumber } from '../FormatHelpers/FormatHelpers';

import * as keyset from './FilterHelpers.i18n';

const i18n = i18nFactory(keyset);

export enum PeriodFilterEqualUrlSearchQueryParams {
    dateRangePreset = GridScreenUrlSearchQueryParams.statPreset,
    dateFrom = GridScreenUrlSearchQueryParams.statFrom,
    dateTo = GridScreenUrlSearchQueryParams.statTo
}

const alignValueToCriteria = (
    criteria: Criteria,
    { value, type }: FilterExpressionValue
): FilterExpressionValue => {
    switch (criteria.type) {
        case CriteriaType.text:
            return {
                type: FilterExpressionValueType.text,
                value
            };
        case CriteriaType.boolean:
            if (
                value === FilterBooleanValueType.ENABLED ||
                value === FilterBooleanValueType.DISABLED
            ) {
                return {
                    type: FilterExpressionValueType.boolean,
                    value: value === FilterBooleanValueType.ENABLED
                };
            }
            return { type, value };
        case CriteriaType.positiveNumber:
            if (typeof value === 'string') {
                return {
                    type: FilterExpressionValueType.number,
                    value:
                        value === ''
                            ? NaN
                            : isNaN(parseNumber(value))
                            ? value
                            : parseNumber(value)
                };
            }

            return { type, value };
        case CriteriaType.date:
            if (typeof value === 'string') {
                if (
                    value.match(
                        /^(([0-9])|([0-2][0-9])|3[0-1])\.([1-9]|(0[1-9])|(1[0-2]))?\.20\d\d$/
                    )
                ) {
                    return {
                        type: FilterExpressionValueType.date,
                        value: moment(value, 'DD.MM.YYYY')
                    };
                } else {
                    return {
                        type: FilterExpressionValueType.partialDate,
                        value
                    };
                }
            }
            return { value, type };
        case CriteriaType.positiveNumberSet:
            return {
                type: FilterExpressionValueType.numberSet,
                value:
                    typeof value === 'string'
                        ? value
                              .split(',')
                              .map(i => i.trim())
                              .filter(i => i !== '')
                        : value
            };
        case CriteriaType.enumSet:
            return getFilterExpressionValueOfEnumSet(value, criteria);
        case CriteriaType.enumSetRadio:
            return getFilterExpressionValueOfEnumSetRadio(value, criteria);
        default:
            throw `unsupported type: ${criteria.type}`;
    }
};

export const getFilterExpressionValueOfEnumSet = (
    value: FilterExpressionValueData,
    criteria: Criteria
): FilterExpressionValue => {
    return {
        type: FilterExpressionValueType.enumSet,
        value:
            typeof value === 'string'
                ? value
                      .split(',')
                      .filter(i => i.trim() !== '')
                      .map(i => {
                          const title = i.trim();

                          return (
                              (criteria.enumSet || []).find(
                                  item =>
                                      item.title.toLowerCase() ===
                                      title.toLowerCase()

                                  // DIRECT-73318: TODO порефачить на key: null
                              ) || { title, key: '' }
                          );
                      })
                : value
    };
};

export const getFilterExpressionValueOfEnumSetRadio = (
    value: FilterExpressionValueData,
    criteria: Criteria
): FilterExpressionValue => {
    let valueResult: FilterExpressionValueData;
    if (typeof value === 'string') {
        const title = value.trim();
        const foundedValue = (criteria.enumSetRadio || []).find(
            item => item.title.toLowerCase() === title.toLowerCase()
        );
        valueResult = foundedValue ? foundedValue : { key: '', title };
    } else {
        valueResult = value;
    }

    return {
        type: FilterExpressionValueType.enumSetRadio,
        value: valueResult
    };
};

const getCriteria = (
    data: Criteria | CriteriaContainer
): Criteria | undefined => ('type' in data ? data : undefined);

const findCurrentCriteriaByExpression = (
    expressionFieldText: string,
    criteria: Array<Criteria | CriteriaContainer>
): Criteria | undefined => {
    for (let i = 0, element, n = criteria.length; i < n; i++) {
        const item = criteria[i];
        const criteriaFieldText = item.title.toLowerCase();

        if (
            criteriaFieldText.indexOf(expressionFieldText) === 0 ||
            expressionFieldText.indexOf(criteriaFieldText) === 0
        ) {
            return getCriteria(item);
        } else if ('criteria' in item) {
            element = findCurrentCriteriaByExpression(
                expressionFieldText,
                item.criteria
            );
        }
        if (element !== undefined) {
            return element;
        }
    }
    return undefined;
};

export const findCriteriaByExpression = (
    expression: FilterPartialExpression,
    criteria: Array<Criteria | CriteriaContainer>
): Criteria | undefined => {
    const fieldExpression = expression.field;
    const expressionFieldText = fieldExpression.toLowerCase();

    return findCurrentCriteriaByExpression(expressionFieldText, criteria);
};

export const findEnumSetFieldsItemByKey = (
    keyEnumSetFieldsItem?: string,
    enumSetFields?: Array<EnumSetItem>
): EnumSetItem | undefined => {
    if (!keyEnumSetFieldsItem || !enumSetFields) {
        return undefined;
    }

    return enumSetFields.find(item => {
        return (
            item.key.indexOf(keyEnumSetFieldsItem) === 0 ||
            keyEnumSetFieldsItem.indexOf(item.key) === 0
        );
    });
};

export const findEnumSetFieldsItemByTitle = (
    titleEnumSetFieldsItem?: string,
    enumSetFields?: Array<EnumSetItem>
): EnumSetItem | undefined => {
    if (!titleEnumSetFieldsItem || !enumSetFields) {
        return undefined;
    }

    const titleEnumSetFieldsItemText = titleEnumSetFieldsItem.toLowerCase();

    return enumSetFields.find(item => {
        const itemTitleText = item.title.toLowerCase();
        return (
            itemTitleText.indexOf(titleEnumSetFieldsItemText) === 0 ||
            titleEnumSetFieldsItemText.indexOf(itemTitleText) === 0
        );
    });
};

export function getFilterCriteriaContainersByView(
    view: GridViewType,
    clientInfo?: ClientInfo,
    hasOnlyCpmCampaigns?: boolean,
    isBidCorrectionSearchEnabled?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] {
    switch (view) {
        case campaigns:
            return getCampaignFilterCriteriaContainers(
                clientInfo,
                isBidCorrectionSearchEnabled,
                hasOnlyCpmCampaigns,
                goals,
                isGoalsStatEnabled
            );

        case groups:
            return getGroupFilterCriteriaContainers(
                clientInfo,
                hasOnlyCpmCampaigns,
                goals,
                isGoalsStatEnabled
            );

        case banners:
            return getBannerFilterCriteriaContainers(
                clientInfo,
                hasOnlyCpmCampaigns,
                goals,
                isGoalsStatEnabled
            );

        case phrases:
            return getPhraseFilterCriteriaContainers(
                clientInfo,
                hasOnlyCpmCampaigns,
                goals,
                isGoalsStatEnabled
            );

        case retargetings:
            return getTargetingFilterCriteriaContainers(clientInfo);

        case feedFilters:
            return getFeedFilterFilterCriteriaContainers(
                clientInfo,
                goals,
                isGoalsStatEnabled
            );

        case usersProfile:
            return getUserProfileFilterCriteriaContainers(clientInfo);

        default:
            return [];
    }
}

export function findAdditionalCriteriaByExpression(
    expression: FilterPartialExpression,
    criteria: Array<Criteria | CriteriaContainer>
): EnumSetItem | undefined {
    const { additionalField } = expression;
    const currentCriteria = findCriteriaByExpression(expression, criteria);

    if (
        !additionalField ||
        !currentCriteria ||
        !currentCriteria.enumSetFields
    ) {
        return undefined;
    }

    return (
        findEnumSetFieldsItemByKey(
            additionalField.key,
            currentCriteria.enumSetFields
        ) ||
        findEnumSetFieldsItemByTitle(
            additionalField.title,
            currentCriteria.enumSetFields
        )
    );
}

export const alignPartialExpressionToCriteria = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression
): FilterPartialExpression => {
    const expressionFieldText = expression.field.toLowerCase();
    const currentCriteria = findCriteriaByExpression(expression, criteria);

    if (expression.operator === undefined && expression.value === undefined) {
        let field = expression.field;
        const additionalField = expression.additionalField;
        const separatorField = expression.separatorField;
        let operator;

        if (currentCriteria) {
            const criteriaFieldText = currentCriteria.title.toLowerCase();

            if (
                expressionFieldText !== criteriaFieldText &&
                expressionFieldText.indexOf(criteriaFieldText) === 0
            ) {
                field = expression.field.substr(0, criteriaFieldText.length);
                operator = expression.field
                    .substr(criteriaFieldText.length)
                    .trim();
            }

            if (
                additionalField !== undefined &&
                additionalField.title !== undefined &&
                currentCriteria.enumSetFields !== undefined
            ) {
                const enumsSetFieldsItem = findEnumSetFieldsItemByTitle(
                    additionalField.title,
                    currentCriteria.enumSetFields
                );
                const criteriaAdditionalField =
                    enumsSetFieldsItem && enumsSetFieldsItem.title;

                if (
                    criteriaAdditionalField !== undefined &&
                    criteriaAdditionalField !== additionalField.title &&
                    additionalField.title.indexOf(criteriaAdditionalField) === 0
                ) {
                    additionalField.title = additionalField.title.substr(
                        0,
                        criteriaAdditionalField.length
                    );
                    operator = additionalField.title
                        .substr(criteriaAdditionalField.length)
                        .trim();
                }
            }
        }

        return {
            field,
            operator,
            separatorField,
            additionalField
        };
    }

    return expression.value && currentCriteria
        ? {
              ...expression,
              value: alignValueToCriteria(currentCriteria, expression.value)
          }
        : expression;
};

export const getSuggestStepByExpression = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression,
    container: CriteriaContainer | undefined
): SuggestStep => {
    const currentCriteria = findCriteriaByExpression(expression, criteria);
    const titleAdditionalItem =
        expression.additionalField && expression.additionalField.title;
    const enumSetFields = currentCriteria && currentCriteria.enumSetFields;
    const shouldHaveAdditionalField = Boolean(enumSetFields);
    const additionalItem = findEnumSetFieldsItemByTitle(
        titleAdditionalItem,
        enumSetFields
    );

    if (expression.field === '') {
        return container !== undefined
            ? SuggestStep.field
            : SuggestStep.container;
    }

    const partialExpression = alignPartialExpressionToCriteria(
        criteria,
        expression
    );

    const { additionalField } = partialExpression;

    // условие необходимо, чтобы при вводе названия цели при пробеле не начинался ввода опреатора,
    // так как пробел является индикатором ввода следующего шага
    if (
        shouldHaveAdditionalField &&
        additionalField &&
        additionalField.title !== undefined &&
        additionalItem &&
        additionalItem.title !== undefined &&
        isPrefixValue(additionalItem.title, additionalField.title) &&
        !isEqualValues(additionalItem.title, additionalField.title)
    ) {
        return SuggestStep.fieldAdditional;
    }

    if (partialExpression.value !== undefined) {
        return SuggestStep.value;
    }

    if (partialExpression.operator !== undefined) {
        return SuggestStep.operator;
    }

    if (
        (partialExpression.separatorField !== undefined ||
            partialExpression.additionalField !== undefined) &&
        currentCriteria &&
        shouldHaveAdditionalField &&
        isEqualValues(partialExpression.field, currentCriteria.title)
    ) {
        return SuggestStep.fieldAdditional;
    }

    return SuggestStep.field;
};

export const isPrefixValue = (text: string, prefix: string): boolean => {
    const preparedText = text.trim().toLowerCase();
    const preparedPrefix = prefix.trim().toLowerCase();

    return preparedText.indexOf(preparedPrefix) === 0;
};

export const isEqualValues = (text: string, prefix: string): boolean => {
    return text.toLowerCase() === prefix.toLowerCase();
};

const isFieldValid = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression
): boolean => {
    return expression.operator !== undefined
        ? findCriteriaByExactTitle(criteria, expression.field) !== undefined
        : findCriteriaByExpression(expression, criteria) !== undefined;
};

const isAdditionalFieldValid = (
    criteria: Array<Criteria | CriteriaContainer>,
    partialExpression: FilterPartialExpression
): boolean => {
    const { additionalField } = partialExpression;

    if (additionalField === undefined || !additionalField.title) {
        return true;
    }
    const additionalFieldText = additionalField.title.toLowerCase();

    return criteria.some(itemCriteria => {
        if ('type' in itemCriteria) {
            if (!itemCriteria.enumSetFields) {
                return false;
            }

            return itemCriteria.enumSetFields.some(itemAdditionalField => {
                const titleItem = itemAdditionalField.title.toLowerCase();

                return (
                    titleItem.indexOf(additionalFieldText) === 0 ||
                    additionalFieldText.indexOf(titleItem) === 0
                );
            });
        }
        return false;
    });
};

const isOperatorValid = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression
): boolean => {
    const operator = expression.operator;
    const additionalField = expression.additionalField;
    const currentCriteria = findCriteriaByExpression(expression, criteria);

    if (!currentCriteria) {
        return false;
    }

    if (
        operator === undefined ||
        (operator === '' && !currentCriteria.enumSetFields)
    ) {
        return true;
    }

    if (currentCriteria.enumSetFields && additionalField === undefined) {
        return false;
    }

    const operators = getOperatorsForCriteriaType(currentCriteria.type);

    if (operator in Operator) {
        return operators.some(o => o === operator);
    } else {
        const operatorsStrings = operators.map(operatorToString);

        return operatorsStrings.some(
            operatorStr => operatorStr.indexOf(operator) === 0
        );
    }
};

const isValueValid = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression
): boolean => {
    const { value } = expression;

    if (value === undefined) {
        return true;
    }

    const currentCriteria = findCriteriaByExpression(expression, criteria);

    if (!currentCriteria) {
        return false;
    }

    switch (currentCriteria.type) {
        case CriteriaType.positiveNumber:
            return typeof value.value === 'number';
        case CriteriaType.date:
            // TODO DIRECT-73320: Перевести на парсер парсинг даты
            return typeof value.value === 'string'
                ? /^(((([0-9])|([0-2][0-9])|3[0-1])\.?)|((([1-9])|([0-2][0-9])|3[0-1])?\.([0-9]|(0[1-9])|(1[0-2]))\.?)|((([1-9])|([0-2][0-9])|3[0-1])?\.([1-9]|(0[1-9])|(1[0-2]))?\.20?\d?\d?))$/.test(
                      value.value
                  ) || value.value === ''
                : (<Moment>value.value).isValid();
        case CriteriaType.enumSet:
            if (Array.isArray(value.value)) {
                const list = <EnumSetItem[]>value.value;

                return list.every((item, pos) => {
                    return (currentCriteria.enumSet || []).some(enumSetItem => {
                        if (pos === list.length - 1) {
                            return (
                                enumSetItem.title
                                    .toLowerCase()
                                    .indexOf(item.title.toLowerCase()) === 0
                            );
                        } else {
                            return enumSetItem.key === item.key;
                        }
                    });
                });
            } else {
                return false;
            }
        case CriteriaType.positiveNumberSet: {
            if (Array.isArray(value.value)) {
                const list = value.value as string[];

                return list.every(item => {
                    return isFinite(+item);
                });
            } else {
                return false;
            }
        }
        default:
            return true;
    }
};

export const isLastSegmentValid = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression
): boolean => {
    const partialExpression = alignPartialExpressionToCriteria(
        criteria,
        expression
    );

    if (partialExpression.value !== undefined) {
        return (
            isValueValid(criteria, partialExpression) &&
            isOperatorValid(criteria, partialExpression) &&
            isFieldValid(criteria, partialExpression) &&
            isAdditionalFieldValid(criteria, partialExpression)
        );
    }
    if (partialExpression.operator !== undefined) {
        return (
            isOperatorValid(criteria, partialExpression) &&
            isFieldValid(criteria, partialExpression) &&
            isAdditionalFieldValid(criteria, partialExpression)
        );
    }

    return (
        isFieldValid(criteria, partialExpression) &&
        isAdditionalFieldValid(criteria, partialExpression)
    );
};

const isExpressionValueValid = (
    expressionCriteria: Criteria,
    { value }: FilterPartialExpression
): boolean => {
    if (
        value === undefined ||
        value.value === undefined ||
        value.value === '' ||
        value.type === FilterExpressionValueType.partialDate ||
        value.type === FilterExpressionValueType.partial
    ) {
        return false;
    }

    return expressionCriteria.validate
        ? expressionCriteria.validate(value)
        : true;
};

export const isExpressionValid = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression,
    prohibitFullText?: boolean
): boolean => {
    const { field, operator } = expression;
    const currentCriteria = findCriteriaByExactTitle(criteria, field);

    if (!currentCriteria) {
        return prohibitFullText ? false : !operator;
    }

    const operators = getOperatorsForCriteriaType(currentCriteria.type);

    if (!operators.some(o => o === operator)) {
        return false;
    }

    return (
        isExpressionValueValid(currentCriteria, expression) &&
        isExpressionAdditionalFieldValid(currentCriteria, expression)
    );
};

export const isExpressionAdditionalFieldValid = (
    expressionCriteria: Criteria,
    { additionalField, separatorField }: FilterPartialExpression
): boolean => {
    if (separatorField === undefined && additionalField === undefined) {
        return true;
    } else if (!additionalField) {
        return false;
    }

    const { enumSetFields } = expressionCriteria;

    if (!enumSetFields) {
        return true;
    }

    const additionalFieldText =
        additionalField.title && additionalField.title.toLowerCase();

    return enumSetFields.some(item => {
        const titleItemText = item.title.toLowerCase();

        return titleItemText === additionalFieldText;
    });
};

export const isExpressionValueEmpty = (
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression
): boolean => {
    if (expression.value) {
        const {
            value: { value, type }
        } = expression;

        switch (type) {
            case FilterExpressionValueType.boolean:
                return value === '';
            case FilterExpressionValueType.number:
                return typeof value === 'string'
                    ? isEmpty(value)
                    : isNaN(value as number);
            default:
                return isEmpty(value);
        }
    }

    return !isTextExpression(criteria, expression);
};

export const validateExpressions = (
    criteria: Array<Criteria | CriteriaContainer>,
    expressions: FilterPartialExpression[],
    prohibitFullText?: boolean
): boolean[] => {
    let hasTextExpressionAppeared = false;
    const validExpressionsForFields = {};

    return expressions.map(expression => {
        const isValid = isExpressionValid(
            criteria,
            expression,
            prohibitFullText
        );

        if (isValid) {
            const isText = isTextExpression(criteria, expression);
            const additionalExpressionField = expression.additionalField
                ? '.' + expression.additionalField.key
                : '';
            const result = isText
                ? !hasTextExpressionAppeared
                : !validExpressionsForFields[
                      expression.field + additionalExpressionField
                  ];

            if (isText) {
                hasTextExpressionAppeared = true;
            } else {
                validExpressionsForFields[
                    expression.field + additionalExpressionField
                ] = true;
            }
            return result;
        } else {
            return false;
        }
    });
};

export const getValidExpressions = (
    criteria: Array<Criteria | CriteriaContainer>,
    expressions: FilterPartialExpression[]
): FilterPartialExpression[] => {
    const validationResult = validateExpressions(criteria, expressions);
    return expressions.filter((expr, index) => validationResult[index]);
};

export function findCriteriaByExactTitle(
    criteria: Array<Criteria | CriteriaContainer>,
    title: string
): Criteria | undefined {
    for (let i = 0, element, n = criteria.length; i < n; i++) {
        const item = criteria[i];
        if (item.title.toLowerCase() === title.trim().toLowerCase()) {
            return getCriteria(item);
        } else if ('criteria' in item) {
            element = findCriteriaByExactTitle(item.criteria, title);
        }
        if (element !== undefined) {
            return element;
        }
    }
    return undefined;
}

export const flattenContainer = (
    arr: (CriteriaContainer | Criteria)[]
): Criteria[] =>
    arr.reduce((acc: Criteria[], next) => {
        if ('criteria' in next) {
            return acc.concat(flattenContainer(next.criteria));
        } else {
            return acc.concat(next);
        }
    }, []);

const getCriteriaContainer = (
    data: Criteria | CriteriaContainer
): CriteriaContainer | undefined => ('criteria' in data ? data : undefined);

export const findCurrentContainer = (
    key: string,
    containers: Array<Criteria | CriteriaContainer>
): CriteriaContainer | undefined => {
    for (let i = 0, element, n = containers.length; i < n; i++) {
        const item = containers[i];
        if (item.key === key) {
            return getCriteriaContainer(item);
        } else if ('criteria' in item) {
            element = findCurrentContainer(key, item.criteria);
        }
        if (element !== undefined) {
            return element;
        }
    }
    return undefined;
};

export interface SuggestConfig {
    itemsCount: number;
    hasPrevStep: boolean;
    hasAccept: boolean;
}

export const getNextSuggestFocus = (
    { itemsCount, hasPrevStep, hasAccept }: SuggestConfig,
    focus: FilterSuggestControlFocusValue | undefined
): FilterSuggestControlFocusValue | undefined => {
    if (focus === undefined) {
        return itemsCount === 0 ? undefined : 0;
    } else if (isNumber(focus)) {
        let nextCycle: FilterSuggestControlFocusValue = 0;

        if (hasAccept) {
            nextCycle = FilterSuggestControlFocus.ACCEPT;
        } else if (hasPrevStep) {
            nextCycle = FilterSuggestControlFocus.BACK;
        }

        return focus + 1 >= itemsCount ? nextCycle : focus + 1;
    } else if (focus === FilterSuggestControlFocus.ACCEPT) {
        return hasPrevStep ? FilterSuggestControlFocus.BACK : 0;
    } else if (focus === FilterSuggestControlFocus.BACK) {
        return 0;
    }

    throw `wrong focus value: ${focus}`;
};

export const getPreviousSuggestFocus = (
    { itemsCount, hasPrevStep, hasAccept }: SuggestConfig,
    focus: FilterSuggestControlFocusValue | undefined
): FilterSuggestControlFocusValue | undefined => {
    if (focus === undefined) {
        return itemsCount === 0 ? undefined : itemsCount - 1;
    } else if (isNumber(focus)) {
        let prevCycle: FilterSuggestControlFocusValue = itemsCount - 1;

        if (hasPrevStep) {
            prevCycle = FilterSuggestControlFocus.BACK;
        } else if (hasAccept) {
            prevCycle = FilterSuggestControlFocus.ACCEPT;
        }

        return focus - 1 < 0 ? prevCycle : focus - 1;
    } else if (focus === FilterSuggestControlFocus.ACCEPT) {
        return itemsCount - 1;
    } else if (focus === FilterSuggestControlFocus.BACK) {
        return hasAccept ? FilterSuggestControlFocus.ACCEPT : itemsCount - 1;
    }

    throw `wrong focus value: ${focus}`;
};

export const getPlaceholderForValue = (value: CriteriaType): string => {
    switch (value) {
        case CriteriaType.positiveNumberSet:
            return i18n('number-set-placeholder');
        case CriteriaType.date:
            return i18n('date-placeholder');
        default:
            return i18n('your-value');
    }
};

export function serializePeriodFilter(
    period: PeriodFilterValue
): Partial<Record<GridScreenUrlSearchQueryParams, null | undefined | string>> {
    const { dateFrom, dateTo, dateRangePreset } = period;

    return {
        [GridScreenUrlSearchQueryParams.statPreset]:
            dateFrom && dateTo ? '' : (dateRangePreset as string),
        [GridScreenUrlSearchQueryParams.statFrom]: dateFrom
            ? dateFrom.format('YYYY-MM-DD')
            : '',
        [GridScreenUrlSearchQueryParams.statTo]: dateTo
            ? dateTo.format('YYYY-MM-DD')
            : ''
    };
}

export function serializePeriodFilterForSourceParams(
    period: PeriodFilterValue
): GridStatPeriod {
    const { dateFrom, dateTo, dateRangePreset } = period;
    return {
        statPreset: dateFrom && dateTo ? undefined : dateRangePreset,
        statFrom: dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined,
        statTo: dateTo ? dateTo.format('YYYY-MM-DD') : undefined
    };
}

export function preparePeriodFilterAfterLocalStorage(
    period?: PeriodFilterValue
): PeriodFilterValue {
    if (!period) {
        return {
            dateRangePreset: DateRangePreset.last30Days
        };
    }

    return {
        ...period,
        dateFrom: period.dateFrom ? moment(period.dateFrom) : undefined,
        dateTo: period.dateTo ? moment(period.dateTo) : undefined
    };
}

export const isExpressionEmpty = (
    expression: FilterPartialExpression
): boolean => {
    return expression.field === '';
};

export function getEmptyExpression(): FilterPartialExpression {
    return {
        field: ''
    };
}

export const serializeFilterToString = (
    expressions: FilterPartialExpression[]
): string => {
    return expressions
        .map(expression => expressionToString(expression, true))
        .join('|');
};

export const parseFilterFromString = (
    criteria: Criteria[],
    str: string,
    isUrlParse?: boolean
): FilterPartialExpression[] => {
    if (str === '') {
        return [];
    }
    const expressions = parse(str);
    const preparedExpressions = expressions.map(currentParsedExpression =>
        convertFromParsedExpressionToExpression(
            currentParsedExpression,
            criteria,
            isUrlParse
        )
    );
    return preparedExpressions.map(expression => {
        return alignPartialExpressionToCriteria(criteria, expression);
    });
};

export const getMatchedTextSuggestItems = (
    expression: FilterPartialExpression,
    items: Array<string> = []
): Array<string> => {
    const value = expression.field;

    return items.filter(item =>
        item.toLowerCase().includes(value.toLowerCase())
    );
};

export function getEnumSetFrom<T>(
    enumObject: T,
    titles: { [key in keyof T]: string }
): Array<EnumSetItem> {
    return Object.keys(enumObject).map(key => {
        return {
            key,
            title: titles[key]
        };
    });
}

export function isTextExpression(
    criteria: Array<Criteria | CriteriaContainer>,
    expression: FilterPartialExpression
): boolean {
    const currentCriteria = findCriteriaByExactTitle(
        criteria,
        expression.field
    );

    return !currentCriteria && !expression.operator;
}

export function getVisibleTextItemsCount(textItemsLength: number): number {
    return textItemsLength < visibleTextItemsCount
        ? textItemsLength
        : visibleTextItemsCount;
}

export function convertFromParsedExpressionToExpression(
    parsedExpression: ParsedFilterPartialExpression,
    currentCriteria: Array<Criteria | CriteriaContainer>,
    isUrlParse?: boolean
): FilterPartialExpression {
    const resultExpression = {
        ...parsedExpression,
        additionalField: undefined
    } as FilterPartialExpression;
    const foundedCriteria = findCriteriaByExpression(
        resultExpression,
        currentCriteria
    );

    if (parsedExpression.additionalField === '') {
        resultExpression.additionalField = {
            key: '',
            title: ''
        };
        return resultExpression;
    }

    if (foundedCriteria && parsedExpression.additionalField) {
        if (isUrlParse) {
            resultExpression.additionalField = {
                key: parsedExpression.additionalField
            };
        } else {
            resultExpression.additionalField = {
                key: '',
                title: parsedExpression.additionalField
            };
        }
    }

    return resultExpression;
}
